// Web-only admin dashboard root. Rendered from App.tsx for any /admin path on the
// web (never inside the native iOS app). It owns the password gate, fetches one
// snapshot from the `adminDashboard` callable, and routes between the sections.
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminDashboard, isWrongPasswordError } from '../../lib/adminApi';
import { AdminError, AdminLogin, AdminProvider, AdminShell, AdminSplash } from './parts';
import Overview from './Overview';
import Users from './Users';
import Picks from './Picks';
import Leagues from './Leagues';
import LeagueDetail from './LeagueDetail';
import MiniLeagues from './MiniLeagues';
import Broadcast from './Broadcast';
import Promos from './Promos';

// Session-only so a refresh keeps you in, but closing the tab signs you back out.
const PW_KEY = 'h2h_admin_pw';

export default function AdminApp() {
  const [password, setPassword] = useState<string | null>(() => sessionStorage.getItem(PW_KEY));

  const query = useQuery({
    queryKey: ['adminDashboard', password],
    queryFn: () => fetchAdminDashboard(password as string),
    enabled: !!password,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false
  });

  // A stored password the server now rejects (e.g. it was rotated) drops us back
  // to the gate. Any other failure (server/internal error) is NOT a bad password —
  // keep the session and surface a real error so the bug isn't hidden as "wrong
  // password".
  const wrongPassword = query.isError && isWrongPasswordError(query.error);
  useEffect(() => {
    if (wrongPassword) {
      sessionStorage.removeItem(PW_KEY);
      setPassword(null);
    }
  }, [wrongPassword]);

  function unlock(pw: string) {
    sessionStorage.setItem(PW_KEY, pw);
    setPassword(pw);
  }

  function lock() {
    sessionStorage.removeItem(PW_KEY);
    setPassword(null);
  }

  if (!password || wrongPassword) return <AdminLogin onUnlock={unlock} />;
  if (query.isError) return <AdminError onRetry={() => void query.refetch()} onLock={lock} />;
  if (!query.data) return <AdminSplash />;

  return (
    <AdminProvider
      value={{
        data: query.data,
        password: password as string,
        isFetching: query.isFetching,
        refetch: () => void query.refetch(),
        lock
      }}
    >
      <AdminShell>
        <Routes>
          <Route path="/admin" element={<Overview />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/picks" element={<Picks />} />
          <Route path="/admin/leagues" element={<Leagues />} />
          <Route path="/admin/leagues/:leagueId" element={<LeagueDetail />} />
          <Route path="/admin/mini-leagues" element={<MiniLeagues />} />
          <Route path="/admin/broadcast" element={<Broadcast />} />
          <Route path="/admin/promos" element={<Promos />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </AdminShell>
    </AdminProvider>
  );
}
